import { AccountInfo } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react'; // 追加
import axios from 'axios';
import { DateTime } from 'luxon';
import { createContext, FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useSWRMutation from 'swr/mutation';

import { CreateSubscriptionsInput, User } from '../API';
import useNotifications from '../hooks/useNotifications';
import { useSettings } from '../hooks/useSettings';
import { axiosRequest, urlBase64ToUint8Array } from '../utils';

export interface LocalStorageContext {
  subscribedAt: string;
}

export interface UserContextValue {
  user?: AccountInfo | undefined; // 変更: OktaのUserClaimsからMSALのAccountInfoへ
  userInfo?: User | undefined;
  notifications: {
    notificationBadgeNum: number;
    taskBadgeNum: number;
  };
  updateNotifications: () => void;
  isInitialized: boolean;
  unReadNum: number;
  updateUnReadNum: (unReadNum: number) => void;
  updateSubscribedAt: () => void;
}

export const UserContext = createContext<UserContextValue>({
  user: undefined,
  userInfo: undefined,
  notifications: {
    notificationBadgeNum: 0,
    taskBadgeNum: 0,
  },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateNotifications: () => {},
  isInitialized: false,
  unReadNum: 0,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateUnReadNum: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateSubscribedAt: () => {},
});

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: FC<UserProviderProps> = (props) => {
  const { children } = props;
  const nagivate = useNavigate();
  const [userInfo, setUserInfo] = useState<User>();
  const [isInitialized, setIsInitialized] = useState(false);
  const { instance, accounts } = useMsal(); // 追加
  const { data: notifications, refetch } = useNotifications();
  const [unReadNum, setUnReadNum] = useState(0);
  const { settings } = useSettings();
  const { pathname } = useLocation();
  const { trigger: postSubscriptions } = useSWRMutation(
    `${process.env.REACT_APP_API_ENDPOINT ?? ''}/users/${userInfo?.id ?? 'invalid_user_id'}/subscriptions`,
    axiosRequest<CreateSubscriptionsInput>
  );

  useEffect(() => {
    const fetchAndSetUserInfo = async () => {
      // Check if authenticated and user info is not already loaded
      if (accounts.length > 0 && !userInfo) {
        try {
          const account = accounts[0];
          const email = account.username; // MSAL uses 'username' for the login identifier, typically the email.

          const request = {
            scopes: [`api://${process.env.REACT_APP_CLIENT_ID ?? ''}/Admins`],
            account,
          };
          const tokenResponse = await instance.acquireTokenSilent(request);

          const res = await axios.get(`${process.env.REACT_APP_API_ENDPOINT ?? ''}/users?email=${email}`, {
            headers: {
              Authorization: `Bearer ${tokenResponse.accessToken}`,
            },
          });

          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (res.data && res.data.length > 0) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            setUserInfo(res.data[0]);
            setIsInitialized(true);
          } else {
            nagivate('/error?code=userNotFound');
          }
        } catch (error) {
          console.error('Failed to fetch user info:', error);
          nagivate('/error');
        }
      } else if (accounts.length === 0) {
        // Handle logout: clear user info
        setUserInfo(undefined);
        setIsInitialized(false);
      }
    };

    fetchAndSetUserInfo().catch(console.error);
  }, [accounts, instance, nagivate, userInfo]);

  const updateSubscription = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const existing = await registration.pushManager.getSubscription();

      if (existing || settings.subscribed) {
        if (existing) {
          await existing.unsubscribe();
        }

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(
            process.env.REACT_APP_VAPID_PUBKEY ?? 'INVALID_PUB_KEY'
          ) as BufferSource,
        });

        if (subscription) {
          const request = {
            scopes: [`api://${process.env.REACT_APP_CLIENT_ID ?? ''}/Admins`],
            account: accounts[0],
          };
          const tokenResponse = await instance.acquireTokenSilent(request);

          await postSubscriptions({
            method: 'POST',
            body: { subscription: JSON.stringify(subscription) },
            token: tokenResponse.accessToken,
          });

          globalThis.localStorage.setItem(
            'ci-tweet-app.context',
            JSON.stringify({ subscribedAt: DateTime.now().toISO() })
          );
        }
      }
    } catch (err) {
      console.error('Failed to update subscription', err);
    }
  }, [instance, accounts, postSubscriptions, settings.subscribed]);

  useEffect(() => {
    try {
      const storedData: string | null = globalThis.localStorage.getItem('ci-tweet-app.context');
      const localContext = (storedData ? JSON.parse(storedData) : { subscribedAt: '' }) as LocalStorageContext;
      if (
        userInfo &&
        (!localContext?.subscribedAt ||
          DateTime.now().diff(DateTime.fromISO(localContext.subscribedAt), ['hours']).hours > 24)
      ) {
        // NOTE: Web push doesn't work some time, https://stackoverflow.com/questions/76282137/web-push-doesnt-work-on-ios-16-4-after-some-time
        // Update if more than 24 hours have passed since last subscribing
        updateSubscription().catch(console.error);
      }
    } catch (err) {
      // If stored data is not a strigified JSON this will fail,
      // that's why we catch the error
    }
  }, [updateSubscription, userInfo, pathname]); // NOTE: Listen pathname changed

  const updateSubscribedAt = useCallback(() => {
    globalThis.localStorage.setItem('ci-tweet-app.context', JSON.stringify({ subscribedAt: DateTime.now().toISO() }));
  }, []);

  useEffect(() => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
      (navigator as any).setAppBadge(notifications.taskBadgeNum + notifications.notificationBadgeNum + unReadNum);
    } catch (e) {
      // do nothing
    }
  }, [unReadNum, notifications]);

  const value = useMemo(
    () => ({
      user: accounts.length > 0 ? accounts[0] : undefined,
      userInfo,
      notifications,
      updateNotifications: refetch,
      isInitialized,
      unReadNum,
      updateUnReadNum: setUnReadNum,
      updateSubscribedAt,
    }),
    [accounts, userInfo, notifications, refetch, isInitialized, unReadNum, updateSubscribedAt]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
